import { useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { InfoCircleFill } from "react-bootstrap-icons";

export interface AddSpacesTimingModalProps {
  show:                boolean;
  onClose:             () => void;
  onConfirm:           (option: "immediate" | "next_cycle") => void;
  nextCycleDate?:      string;
  isAccountsReceivable?: boolean;
}

export function AddSpacesModal({
  show,
  onClose,
  onConfirm,
  nextCycleDate,
  isAccountsReceivable = false,
}: AddSpacesTimingModalProps) {
  const [selected, setSelected] = useState<"immediate" | "next_cycle" | null>(null);

  const nextCycleLabel = nextCycleDate
    ? `Wait until next invoice cycle (${nextCycleDate})`
    : "Wait until next invoice cycle";

  if (!isAccountsReceivable) {
    return (
      <Modal show={show} onHide={onClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add spaces now or later?</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Check
              type="radio"
              id="apply-immediately"
              name="applyOption"
              label={
                <>
                  <strong>Add Immediately</strong> — Add spaces now.
                </>
              }
              value="immediate"
              checked={selected === "immediate"}
              onChange={() => setSelected("immediate")}
              className="mb-3"
            />

            <Form.Check
              type="radio"
              id="next-billing-cycle"
              name="applyOption"
              label={
                <>
                  <strong>Next Billing Cycle</strong> — Add spaces starting with
                  your next renewal cycle.
                </>
              }
              value="next_cycle"
              checked={selected === "next_cycle"}
              onChange={() => setSelected("next_cycle")}
            />
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="btn-main"
            disabled={!selected}
            onClick={() => selected && onConfirm(selected)}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  const infoCopy = (
    <>If selecting <strong>Apply additional spaces to current invoice</strong>, you will receive an amended or new invoice with the additional spaces included.</>
  );

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>When to Apply Additional Spaces</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p>Do you want to apply additional spaces now, or wait until the next invoice cycle?</p>

        <div className="rounded-3 p-3 mb-3 d-flex gap-2 align-items-start small"
             style={{ backgroundColor: 'var(--color-light)' }}>
          <InfoCircleFill className="flex-shrink-0 mt-1" style={{ color: 'var(--color-mid)' }} />
          <span>{infoCopy}</span>
        </div>

        <Form>
          <Form.Check
            type="radio"
            id="apply-invoice"
            name="applyOption"
            label="Apply additional spaces to current invoice"
            value="immediate"
            checked={selected === "immediate"}
            onChange={() => setSelected("immediate")}
            className="mb-3"
          />

          <Form.Check
            type="radio"
            id="next-invoice-cycle"
            name="applyOption"
            label={nextCycleLabel}
            value="next_cycle"
            checked={selected === "next_cycle"}
            onChange={() => setSelected("next_cycle")}
          />
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="btn-main"
          disabled={!selected}
          onClick={() => selected && onConfirm(selected)}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
